import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import { UIDealDetailsTabs, useStore, useStoreUI } from '@assets/model/Store';
import { useSelectionStore } from '@assets/model/StoreSelection';
import { useNavUtils } from '@assets/utils/NavUtils';
import { observer } from 'mobx-react';
import { Flex } from '../UI/Flex/Flex';
import { useLocation } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { Panel } from '../Panel/Panel';
import styles from './SaveForTests.module.less';
import { getAuth } from 'firebase/auth';
import { ModalFlowiseRerunStep } from '../ModalFlowiseRerunStep/ModalFlowiseRerunStep';
import { ref, remove } from 'firebase/database';

// Component for "Save for Tests" button in the admin panel
const AdminSaveForTestsButton = observer(() => {
    const store = useStore();
    const selectionStore = useSelectionStore();

    // Only show button to admin users and when a report is selected
    if (!store.isAdmin || !store.selectedReportId) {
        return null;
    }

    // Disable the button when selection mode is already active
    const isDisabled = selectionStore.isSelectionModeActive;

    return (
        <Flex className={styles.saveForTestsContainer}>
            <button
                onClick={() => selectionStore.activateSelectionMode()}
                disabled={isDisabled}
                className={styles.saveForTestsButton}
            >
                Save for Tests
            </button>
        </Flex>
    );
});

export const AdminSection = observer(() => {
    const store = useStore();
    const storeUI = useStoreUI();
    const location = useLocation();
    const isRoot = location.pathname === '/';

    const nav = useNavUtils();

    const getCompanyData = (reportId: string) => {
        const q = store.createDataAccess(reportId);
        const year = q.actualYear();
        const company_overview = q.value('company_overview');
        const company_overview_short = q.value('company_overview_short');
        const industry = q.value('industry');
        const subIndustry = q.value('industrySubType');

        const ebitda = q.valueByYear('ebitda_per_year', year);
        const ebitdaMargin = q.valueByYearDivideByRevenue(
            'ebitda_per_year',
            year
        );

        const netRevenue = q.valueByYear('net_revenue_per_year', year);
        const grossProfit = q.valueByYear('gross_profit_per_year', year);

        const ebitdaMargin2YearsAgo = q.valueByYearDivideByRevenue(
            'ebitda_per_year',
            year,
            -2
        );

        //const adjustedEbidta = q.valueByYear('adjusted_ebitda_per_year', year);

        const totalCapexByRev = q.valueByYearDivideByRevenue(
            'capex_per_year',
            year
        );

        const gross_retention = q.valueByYearDivideByRevenue(
            'gross_retention_lost_only_per_year',
            year
        );

        const netRet = q.valueByYear('net_retention_yearly', year);

        const capexPerYear = q.valueByYear('capex_per_year', year);
        const maintenanceCapexPerYear = q.valueByYear(
            'maintenance_capex_per_year',
            year
        );

        const freeCashFlow = q.free_cash_flow(year);

        const totalAssets = q.valueByYear('total_current_assets_yearly', year);
        const totalLiabilities = q.valueByYear(
            'total_current_liabilities_yearly',
            year
        );

        const tam = q.value('total_addressable_market');
        const historicalMarketGrowth = q.value('historical_market_growth');
        const projectedMarketGrowth = q.value('projected_market_growth');
        const contractualRevenue = q.value('recurring_revenue_summary');

        const rows = [];
        const data = {};

        const addValue = (name, value) => {
            data[name] = value;
            //rows.push(`${name},${value}`);
        };

        addValue('company_name', q.value('company_name'));
        addValue('company_overview', company_overview);
        addValue('company_overview_short', company_overview_short);
        addValue('industry', industry);
        addValue('sub_industry', subIndustry);
        addValue('net_revenue', netRevenue);
        addValue('gross_profit', grossProfit);
        addValue('organic_cagr_2_years', q.organicCagr(2));
        addValue('projected_organic_cagr_2_years', q.projectedCagr(2));
        addValue('organic_cagr_3_years', q.organicCagr(3));
        addValue('projected_organic_cagr_3_years', q.projectedCagr(3));
        addValue('organic_cagr_4_years', q.organicCagr(4));
        addValue('projected_organic_cagr_4_years', q.projectedCagr(4));
        addValue('organic_cagr_5_years', q.organicCagr(5));
        addValue('projected_organic_cagr_5_years', q.projectedCagr(5));
        addValue('ebitda', ebitda);
        addValue('ebitda_margin', ebitdaMargin);
        addValue('ebitda_margin_2_years_ago', ebitdaMargin2YearsAgo);
        //addValue('adjusted_ebitda', adjustedEbidta);
        addValue('total_capex_by_rev', totalCapexByRev);
        addValue('gross_retention', gross_retention);
        addValue('net_retention', netRet);
        addValue('capex_per_year', capexPerYear);
        addValue('maintenance_capex_per_year', maintenanceCapexPerYear);
        addValue('free_cash_flow', freeCashFlow);
        addValue('total_assets', totalAssets);
        addValue('total_liabilities', totalLiabilities);
        addValue('tam', tam);
        addValue('historical_market_growth', historicalMarketGrowth);
        addValue('projected_market_growth', projectedMarketGrowth);
        addValue('contractual_revenue', contractualRevenue);

        return data;
    };

    function jsonToTransposedCSV(jsonData) {
        const companyIds = Object.keys(jsonData);
        const fieldSet = new Set();

        // Collect all unique fields
        companyIds.forEach(id => {
            Object.keys(jsonData[id]).forEach(field => fieldSet.add(field));
        });
        companyIds.sort();

        const fields = Array.from(fieldSet);

        // CSV Header
        let csv = `Field||${companyIds.join('||')}\n`;

        // Add rows for each field
        fields.forEach((field: string) => {
            const row = [field];
            companyIds.forEach(id => {
                row.push(
                    jsonData[id][field] !== undefined ? jsonData[id][field] : ''
                );
            });
            csv += row.join('||') + '\r\n';
        });

        return csv;
    }

    const genAllCompaniesData = () => {
        const companies = Object.keys(store.reportDataDB);
        const data = {};
        companies.sort();

        companies.forEach((company, i) => {
            data[company] = getCompanyData(company);
        });

        var csv = jsonToTransposedCSV(data);
        navigator.clipboard.writeText(csv);
    };

    const getCurrentReportFilename = () => {
        const fb = getFirebase();
        return store.query.value('pdf');
    };

    const getCurrentReport = () => {
        return store.selectedReportId;
    };

    const getAllFileame = () => {
        return '**ALL REPORTS**';
    };
    const getLastJobName = () => {
        const fb = getFirebase();
        const sorted = Object.entries(store.jobs).sort((a, b) =>
            b[0] > a[0] ? 1 : -1
        );

        const pdfFile = sorted[0][1]['fileName'];
        return pdfFile;
    };

    if (!store.showAdminConsole) {
        return null;
    }

    type RerunTypes = 'LAST JOB' | 'CURRENT' | 'ALL' | 'FLOWISE';

    const makeReRunButtons = (
        typeRerun: RerunTypes,
        getFileName: () => string
    ) => {
        const extraSure = '*** FULL-RERUN: DOUBLE-CHECK: Are you sure?';

        const processRerun = (
            type: RerunTypes,
            pdfFile: string,
            clearCache: boolean
        ) => {
            const fb = getFirebase();
            if (type == 'ALL') {
                var areYouSure2 = window.confirm(extraSure);
                if (!areYouSure2) return;

                const fb = getFirebase();
                var reports = store.reportDataDB;
                var redoList = [];
                for (var reportId in reports) {
                    var data = reports[reportId];
                    if (data['status'] == 'completed') {
                        redoList.push(data['pdf']);
                    }
                }
                redoList.forEach(pdfFile => {
                    fb.add_job(pdfFile, null, null, clearCache);
                });
            } else {
                fb.add_job(pdfFile, null, null, clearCache);
            }
            nav.home();
        };
        const variant = typeRerun === 'ALL' ? 'danger' : 'light';
        return (
            <Flex direction="column" style={{ gap: '10px' }}>
                <Panel shadow={true}>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            backgroundColor: 'lightgray',
                            borderRadius: '10px 10px 0px 0px',
                            padding: '5px'
                        }}
                    >
                        {typeRerun}
                    </div>
                    <Flex style={{ gap: '2px' }}>
                        <Button
                            variant={variant}
                            onClick={() => {
                                const buildMessage = `*** RERUN:  ${typeRerun}: File: ${getFileName()} - Are you sure?`;
                                var cacheStr = '\nUSING SAME CACHE';
                                var areYouSure = window.confirm(
                                    buildMessage + cacheStr
                                );

                                if (!areYouSure) return;

                                processRerun(typeRerun, getFileName(), false);
                            }}
                        >
                            Reuse
                        </Button>

                        <Button
                            variant={variant}
                            onClick={() => {
                                const buildMessage = `*** RERUN:  ${typeRerun}: File: ${getFileName()} - Are you sure?`;
                                var cacheStr = '**** CLEARING CACHE ****';
                                var areYouSure = window.confirm(
                                    buildMessage + cacheStr
                                );

                                if (!areYouSure) return;

                                processRerun(typeRerun, getFileName(), true);
                            }}
                        >
                            Clear
                        </Button>
                    </Flex>
                </Panel>
            </Flex>
        );
    };

    const makeFlowiseRerun = (typeRerun: RerunTypes) => {
        const variant = typeRerun === 'ALL' ? 'danger' : 'light';
        return (
            <Flex direction="column" style={{ gap: '10px' }}>
                <Panel shadow={true}>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            backgroundColor: 'lightgray',
                            borderRadius: '10px 10px 0px 0px',
                            padding: '5px'
                        }}
                    >
                        {typeRerun}
                    </div>
                    <Flex style={{ gap: '2px' }}>
                        <Button
                            variant={variant}
                            style={{ width: '150px' }}
                            onClick={() => {
                                if (store.query.report_has_pdf) {
                                    alert('This only works for no-cim for now');
                                    return;
                                }
                                storeUI.modal = {
                                    title:
                                        'Flowise Rerun Step: ' +
                                        store.selectedReportId,
                                    el: <ModalFlowiseRerunStep />
                                };
                            }}
                        >
                            SELECT STEP(s)
                        </Button>
                    </Flex>
                </Panel>
            </Flex>
        );
    };

    const makeDeleteButton = (getReportName: () => string) => {
        const processDelete = (reportId: string) => {
            const fb = getFirebase();
            fb.deleteReport(reportId);
            nav.home();
        };
        const variant = 'danger';
        return (
            <Flex direction="column" style={{ gap: '10px' }}>
                <Panel shadow={true}>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            backgroundColor: 'lightgray',
                            borderRadius: '10px 10px 0px 0px',
                            padding: '5px'
                        }}
                    >
                        CURRENT
                    </div>
                    <Flex style={{ gap: '2px' }}>
                        <Button
                            variant={variant}
                            onClick={() => {
                                const buildMessage = `*** DELETE: Report: ${getReportName()} - Are you sure?`;
                                var areYouSure = window.confirm(buildMessage);

                                if (!areYouSure) return;

                                processDelete(getReportName());
                            }}
                        >
                            Delete
                        </Button>
                    </Flex>
                </Panel>
            </Flex>
        );
    };

    const makeClearFlowVars = (getReportName: () => string) => {
        const processClear = (reportId: string) => {
            const fb = getFirebase();
            const path = 'flowise_vars/' + reportId;
            remove(ref(fb.db, path));
        };
        const variant = 'light';
        return (
            <Flex direction="column" style={{ gap: '10px' }}>
                <Panel shadow={true}>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            backgroundColor: 'lightgray',
                            borderRadius: '10px 10px 0px 0px',
                            padding: '5px',
                            width: '110px'
                        }}
                    >
                        CURRENT
                    </div>
                    <Flex style={{ gap: '2px' }}>
                        <Button
                            variant={variant}
                            onClick={() => {
                                const buildMessage = `*** CLEAR FLOWVARS: Report: ${getReportName()} - Are you sure?`;
                                var areYouSure = window.confirm(buildMessage);

                                if (!areYouSure) return;

                                processClear(getReportName());
                            }}
                        >
                            CLEAR VARS
                        </Button>
                    </Flex>
                </Panel>
            </Flex>
        );
    };

    const makeRunEvals = () => {
        const processRunEvals = () => {
            const reportId = store.selectedReportId;
            const taskId = store.selectedTaskId;

            const fb = getFirebase();
            fb.flowtask_add_rerun_evals_job(reportId, taskId);
        };
        const variant = 'light';
        return (
            <Flex direction="column" style={{ gap: '10px' }}>
                <Panel shadow={true}>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            backgroundColor: 'lightgray',
                            borderRadius: '10px 10px 0px 0px',
                            padding: '5px',
                            width: '110px'
                        }}
                    >
                        {store.selectedTaskId}
                    </div>
                    <Flex style={{ gap: '2px' }}>
                        <Button
                            variant={variant}
                            onClick={() => {
                                const buildMessage = `*** RERUN EVALS - Are you sure?`;
                                var areYouSure = window.confirm(buildMessage);

                                if (!areYouSure) return;

                                processRunEvals();
                            }}
                        >
                            RERUN EVALS
                        </Button>
                    </Flex>
                </Panel>
            </Flex>
        );
    };

    const makePauseResumeJobs = () => {
        type status = 'pending' | 'paused';
        const setStatusToJobs = (fromStatus: status, toStatus: status) => {
            const fb = getFirebase();
            const jobs = store.jobs;
            const jobIds = Object.keys(jobs);
            jobIds.forEach(jobId => {
                const job = jobs[jobId];
                if (job['status'] == fromStatus) {
                    fb.setJobStatus(jobId, toStatus);
                }
            });
        };
        const variant = 'danger';
        return (
            <Flex
                direction="column"
                style={{
                    gap: '10px',
                    minWidth: '160px'
                }}
            >
                <Panel shadow={true}>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            backgroundColor: 'lightgray',
                            borderRadius: '10px 10px 0px 0px',
                            padding: '5px'
                        }}
                    >
                        PAUSE/RESUME
                    </div>
                    <Flex style={{ gap: '10px' }}>
                        <Button
                            variant={variant}
                            onClick={() => {
                                const buildMessage = `*** This will PAUSE jobs that are in the QUEUE?`;
                                var areYouSure = window.confirm(buildMessage);

                                if (!areYouSure) return;

                                setStatusToJobs('pending', 'paused');
                            }}
                        >
                            Pause
                        </Button>
                        <Button
                            variant={variant}
                            onClick={() => {
                                const buildMessage = `*** This will RESUME jobs that were previously PAUSED?`;
                                var areYouSure = window.confirm(buildMessage);

                                if (!areYouSure) return;

                                setStatusToJobs('paused', 'pending');
                            }}
                        >
                            Resume
                        </Button>
                    </Flex>
                </Panel>
            </Flex>
        );
    };

    const makeClearJobQueue = () => {
        const processClearJobQueue = () => {
            const fb = getFirebase();
            fb.clearJobQueue();
            nav.home();
        };
        const variant = 'warning';
        return (
            <Flex direction="column" style={{ gap: '10px', minWidth: '100px' }}>
                <Panel shadow={true}>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            backgroundColor: 'lightgray',
                            borderRadius: '10px 10px 0px 0px',
                            padding: '5px'
                        }}
                    >
                        JOB QUEUE
                    </div>

                    <Button
                        variant={variant}
                        onClick={() => {
                            const buildMessage = `*** Clearing Job Queue - Are you sure?`;
                            var areYouSure = window.confirm(buildMessage);

                            if (!areYouSure) return;

                            processClearJobQueue();
                        }}
                    >
                        CLEAR
                    </Button>
                </Panel>
            </Flex>
        );
    };

    const makeStats = () => {
        var totalReports = 0;
        var totalQueued = 0;
        if (!store.reportDataDB) return <></>;

        for (var reportId in store.reportDataDB) {
            const report = store.reportDataDB[reportId];
            if (report['status'] == 'completed') {
                totalReports++;
            }
        }

        for (var jobId in store.jobs ?? {}) {
            const job = store.jobs[jobId];
            if (job['status'] == 'pending') {
                totalQueued++;
            }
        }

        return (
            <Panel shadow={true}>
                <Flex
                    style={{
                        gap: '2px',
                        width: '150px',
                        height: '69px',
                        backgroundColor: 'white',
                        borderRadius: '10px'
                    }}
                    direction="column"
                    alignItems="center"
                    justify="around"
                >
                    <div style={{ fontSize: '18px' }}>
                        <b>Reports: {totalReports}</b>
                    </div>
                    <div style={{ fontSize: '18px' }}>
                        <b>Queue: {totalQueued}</b>
                    </div>
                </Flex>
            </Panel>
        );
    };

    const processLogout = () => {
        const auth = getAuth(getFirebase().app);
        auth.signOut();
        store.logout();
    };
    return (
        <div
            style={{
                transform: 'scale(0.8, 0.8)',
                position: 'absolute',
                left: 0,
                bottom: 0,
                width: '500px',
                height: '100px',
                zIndex: 50000
            }}
        >
            <Flex alignItems="end" fillCell={true} style={{ gap: '20px' }}>
                {!isRoot && makeRunEvals()}
                {!isRoot && makeClearFlowVars(getCurrentReport)}
                {!isRoot && <AdminSaveForTestsButton />}
                {makeReRunButtons('LAST JOB', getLastJobName)}
                {!isRoot &&
                    makeReRunButtons('CURRENT', getCurrentReportFilename)}
                {!isRoot && makeFlowiseRerun('FLOWISE')}
                {!isRoot && makeDeleteButton(getCurrentReport)}
                {makeStats()}
                {/* {makeClearJobQueue()} */}
                {/* {makePauseResumeJobs()} */}
                {/* <input
                    type="button"
                    value="Export"
                    onClick={() => {
                        genAllCompaniesData();
                    }}
                /> */}
                <Button
                    variant="primary"
                    onClick={() => {
                        nav.navigateTo('/admin/backtesting');
                    }}
                >
                    Backtesting Dashboard
                </Button>
                <Button
                    variant="danger"
                    onClick={() => {
                        const buildMessage = `*** Logout - Are you sure?`;
                        var areYouSure = window.confirm(buildMessage);

                        if (!areYouSure) return;

                        processLogout();
                    }}
                >
                    Logout
                </Button>
            </Flex>
        </div>
    );
});
